import { useState } from 'react';
import { useSearch } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowRight, ExternalLink, Table } from 'lucide-react';
import { dataSource } from '@/lib/dataSource';
import { useLocation } from 'wouter';

export function SearchResolve() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [resolution, setResolution] = useState<any>(null);
  const [, setLocation] = useLocation();
  
  const { data: searchResults, isLoading } = useSearch(searchQuery);

  const handleSelectResult = async (result: any) => {
    setSelectedItem(result);
    
    try {
      if (result.type === 'TYPE') {
        const res = await dataSource.resolve(result.key);
        setResolution(res);
      } else if (result.type === 'CODE') {
        const [type, code] = result.key.split(':');
        const res = await dataSource.resolve(type, code);
        setResolution(res);
      }
    } catch (error) {
      console.error('Error resolving path:', error);
    }
  };

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Search & Resolve</h2>
        <p className="text-gray-600 mt-1 text-sm md:text-base">Global search with path resolution and navigation</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 md:gap-6">
        {/* Search Panel */}
        <div className="xl:col-span-2">
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Query</label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search by name, code, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                  <div className="absolute left-3 top-2.5">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Search Results */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">Search Results</h3>
                {isLoading && searchQuery && (
                  <div className="text-sm text-gray-500">Searching...</div>
                )}
                {searchResults && searchResults.length > 0 ? (
                  searchResults.map((result) => (
                    <div
                      key={result.key}
                      className="border border-gray-200 rounded-lg p-3 md:p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleSelectResult(result)}
                    >
                      <div className="flex items-start md:items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {result.type === 'TYPE' ? (
                            <i className="fas fa-folder text-blue-600 text-sm"></i>
                          ) : (
                            <i className="fas fa-tag text-blue-600 text-sm"></i>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                            <span className="font-medium text-gray-900 truncate">{result.name}</span>
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {result.code && (
                                <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded">
                                  {result.code}
                                </span>
                              )}
                              <Badge variant={result.type === 'TYPE' ? 'default' : 'secondary'}>
                                {result.type}
                              </Badge>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 mt-1 truncate">{result.path}</div>
                        </div>
                        <div className="text-xs text-gray-500 flex-shrink-0">
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                  ))
                ) : searchQuery && !isLoading ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    No results found for "{searchQuery}"
                  </div>
                ) : !searchQuery ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    Enter a search query to find types and codes
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Resolve Panel */}
        <div className="xl:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Resolve</CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              <div className="space-y-4">
                {selectedItem ? (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-900 mb-2">Selected Item</div>
                      <div className="text-sm text-blue-800">{selectedItem.name}</div>
                      {selectedItem.code && (
                        <div className="text-xs text-blue-600 mt-1 font-mono">{selectedItem.code}</div>
                      )}
                    </div>
                    
                    {resolution && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700">Full Path</div>
                        <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg font-mono">
                          {resolution.fullPath}
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">Actions</div>
                      <div className="space-y-2">
                        <Button 
                          variant="outline" 
                          className="w-full justify-start text-sm"
                          onClick={() => {
                            // Navigate to CodeManager with selected item context
                            if (selectedItem.type === 'CODE') {
                              localStorage.setItem('cm:selectedItem', JSON.stringify({
                                type: selectedItem.type,
                                typeKey: selectedItem.key.split(':')[0],
                                code: selectedItem.key.split(':')[1],
                                expandPath: resolution?.fullPath
                              }));
                              setLocation('/codes');
                            } else {
                              localStorage.setItem('cm:selectedItem', JSON.stringify({
                                type: selectedItem.type,
                                typeKey: selectedItem.key,
                                expandPath: resolution?.fullPath
                              }));
                              setLocation('/types');
                            }
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          <span className="truncate">Navigate to Item</span>
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full justify-start text-sm"
                          onClick={() => {
                            // Navigate to TreeExplorer with item context
                            localStorage.setItem('cm:selectedItem', JSON.stringify({
                              type: selectedItem.type,
                              typeKey: selectedItem.type === 'CODE' ? selectedItem.key.split(':')[0] : selectedItem.key,
                              code: selectedItem.type === 'CODE' ? selectedItem.key.split(':')[1] : undefined,
                              expandPath: resolution?.fullPath
                            }));
                            setLocation('/');
                          }}
                        >
                          <Table className="h-4 w-4 mr-2" />
                          <span className="truncate">Show in Tree</span>
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-gray-500 text-center py-8">
                    Select a search result to see its resolution details
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
